import { useCallback, useEffect, useState } from 'react';
import type { Prompt, PromptSlot } from '@ozon-ai/ai/contracts';
import { PROMPT_SLOT_LABELS } from '@ozon-ai/ai/contracts';
import { Button } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Label } from '@/components/ui/label.tsx';
import { api } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useTelegram } from '../../../context/TelegramContext';
import { SETTINGS_ACCESS } from '../settings-paths';
import {
  Field,
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
  SettingsSuccessAlert,
  TextAreaField,
} from '../settings-shared';

const EMPTY_FORM = {
  name: '',
  slot: 'general' as PromptSlot,
  system: '',
  userTemplate: '',
  isDefault: false,
};

export function SettingsPromptsPage() {
  const { webApp } = useTelegram();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      setError(null);
      const items = await api.get<Prompt[]>('/prompts');
      setPrompts(items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить промпты');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function startCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMessage(null);
  }

  function startEdit(prompt: Prompt) {
    setEditingId(prompt.id);
    setForm({
      name: prompt.name,
      slot: prompt.slot as PromptSlot,
      system: prompt.system,
      userTemplate: prompt.userTemplate || '',
      isDefault: prompt.isDefault,
    });
    setMessage(null);
  }

  async function save() {
    if (!form.name.trim() || !form.system.trim()) {
      setError('Нужны название и системный текст');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    const payload = {
      name: form.name.trim(),
      slot: form.slot,
      system: form.system.trim(),
      userTemplate: form.userTemplate,
      isDefault: form.isDefault,
    };
    try {
      if (editingId) {
        await api.put('/prompts', { id: editingId, ...payload });
        setMessage('Промпт сохранён');
      } else {
        const created = await api.post<Prompt>('/prompts', payload);
        setEditingId(created.id);
        setMessage('Промпт создан');
      }
      webApp?.HapticFeedback?.notificationOccurred('success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/prompts?id=${encodeURIComponent(id)}`);
      if (editingId === id) {
        startCreate();
      }
      webApp?.HapticFeedback?.notificationOccurred('success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка удаления');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <SettingsSubPageShell title="Промпты" backTo={SETTINGS_ACCESS}>
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}
      {message && <SettingsSuccessAlert>{message}</SettingsSuccessAlert>}

      <SettingsFormCard>
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="font-semibold">{editingId ? 'Редактирование' : 'Новый промпт'}</span>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={startCreate}>
              Новый
            </Button>
          )}
        </div>
        <Field
          label="Название"
          id="promptName"
          value={form.name}
          onChange={(name) => setForm((prev) => ({ ...prev, name }))}
        />
        <div className="mb-3 flex flex-col gap-2">
          <Label>Слот</Label>
          <div className="flex w-full gap-2">
            {(['general', 'analysis'] as PromptSlot[]).map((slot) => (
              <Button
                key={slot}
                type="button"
                variant={form.slot === slot ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setForm((prev) => ({ ...prev, slot }))}
              >
                {PROMPT_SLOT_LABELS[slot]}
              </Button>
            ))}
          </div>
        </div>
        <TextAreaField
          label="Системный промпт"
          id="promptSystem"
          value={form.system}
          onChange={(system) => setForm((prev) => ({ ...prev, system }))}
        />
        <TextAreaField
          label="Шаблон пользователя ({{переменные}})"
          id="promptUser"
          value={form.userTemplate}
          onChange={(userTemplate) => setForm((prev) => ({ ...prev, userTemplate }))}
        />
        <div className="mb-3 flex items-center gap-2">
          <Checkbox
            id="promptDefault"
            checked={form.isDefault}
            onCheckedChange={(checked) =>
              setForm((prev) => ({ ...prev, isDefault: checked === true }))
            }
          />
          <Label htmlFor="promptDefault">По умолчанию для слота</Label>
        </div>
        <Button type="button" className="w-full" disabled={busy} onClick={() => void save()}>
          Сохранить
        </Button>
      </SettingsFormCard>

      {prompts.length === 0 ? (
        <SettingsFormCard>
          <SettingsHint className="text-center">Промптов нет.</SettingsHint>
        </SettingsFormCard>
      ) : (
        prompts.map((prompt) => (
          <SettingsFormCard key={prompt.id}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="flex-1 font-bold">{prompt.name}</span>
              <SettingsHint className="shrink-0 text-right">
                {PROMPT_SLOT_LABELS[prompt.slot] || prompt.slot}
                {prompt.isDefault ? ' · default' : ''}
              </SettingsHint>
            </div>
            <SettingsHint className="line-clamp-2">{prompt.system}</SettingsHint>
            <div className="mt-2 flex gap-2">
              <Button type="button" variant="outline" className="flex-1" size="sm" onClick={() => startEdit(prompt)}>
                Открыть
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                size="sm"
                disabled={busy}
                onClick={() => void remove(prompt.id)}
              >
                Удалить
              </Button>
            </div>
          </SettingsFormCard>
        ))
      )}
    </SettingsSubPageShell>
  );
}
